import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { $apiV1, $apiV2 } from "../../../../config/api";
import {
  getUniqueArrayObjectsByKey,
} from "../../../../helpers";
import {
  applyTemplateParameterIdsToGroups,
  createMatchesCountOptions,
  deriveCompetitionsFromMatches,
  deriveSelectedMatches,
  getSelectedMatchIds,
} from "../helpers/pageFilters";
import { normalizeSeasonSelectionIds } from "../../shared/utils/seasonSelection";
import {
  excludeAttackingTypesParameterGroups,
  excludeAttackingTypesParameters,
} from "../../../../helpers/teamParameterGroups";

const DEFAULT_TEAM_MATCHES_PARAMETER_LIMIT = 12;

const groupTeamParameters = (parameters = []) => {
  const groupedParameters = excludeAttackingTypesParameters(parameters)
    .sort((a, b) => {
      if (!a?.group) return 1;
      if (!b?.group) return -1;
      return Number(a.group.id) - Number(b.group.id);
    })
    .reduce((groups, parameter) => {
      if (!parameter?.group?.name) {
        return groups;
      }

      if (!groups[parameter.group.name]) {
        groups[parameter.group.name] = [];
      }

      groups[parameter.group.name].push({ ...parameter, checked: true });
      return groups;
    }, {});

  return Object.entries(groupedParameters).map(([groupName, items]) => [
    groupName,
    items.sort((a, b) => Number(a?.sort_order ?? a?.id ?? 0) - Number(b?.sort_order ?? b?.id ?? 0)),
  ]);
};

const countCheckedParams = (parameterGroups = []) =>
  parameterGroups.reduce(
    (total, group) =>
      total + (Array.isArray(group?.[1]) ? group[1].filter((item) => item.checked).length : 0),
    0
  );

const countAllParams = (parameterGroups = []) =>
  parameterGroups.reduce(
    (total, group) => total + (Array.isArray(group?.[1]) ? group[1].length : 0),
    0
  );

const applyDefaultMatchesParameterSelection = (parameterGroups = []) => {
  const allParamsCount = countAllParams(parameterGroups);
  const checkedParamsCount = countCheckedParams(parameterGroups);

  if (
    allParamsCount <= DEFAULT_TEAM_MATCHES_PARAMETER_LIMIT ||
    (checkedParamsCount > 0 && checkedParamsCount < allParamsCount)
  ) {
    return parameterGroups;
  }

  let selectedCount = 0;
  return parameterGroups.map((group) => {
    const groupName = group?.[0];
    const groupParameters = Array.isArray(group?.[1]) ? group[1] : [];

    return [
      groupName,
      groupParameters.map((parameter) => {
        selectedCount += 1;
        return {
          ...parameter,
          checked: selectedCount <= DEFAULT_TEAM_MATCHES_PARAMETER_LIMIT,
        };
      }),
    ];
  });
};

const buildSelectedParametersFromGroups = (parameterGroups = []) => {
  const checkedIds = parameterGroups.flatMap((group) =>
    group[1].filter((item) => item.checked).map((item) => item.id)
  );
  const checkedParams = parameterGroups.flatMap((group) =>
    group[1].filter((item) => item.checked)
  );
  const allIds = parameterGroups.flatMap((group) => group[1].map((item) => item.id));

  return {
    allIds,
    selectedParameters: {
      checkedIds,
      params: checkedParams,
    },
  };
};

export const useTeamPageFilterController = ({
  teamId,
  availableSeasons = [],
  selectedSeasonIds = [],
  syncSelectedSeasons,
  l,
  initialMatches = null,
  initialParameters = null,
  shouldEnrichMatches = false,
  isLoadingInitialData = false,
}) => {
  const [loadingParameters, setLoadingParameters] = useState(true);
  const [matches, setMatches] = useState([]);
  // Tracks whether we've already consumed the pre-loaded initial data from v5
  const initialMatchesConsumedRef = useRef(false);
  const completedEnrichmentRequestKeysRef = useRef(new Set());
  const inFlightEnrichmentRequestKeysRef = useRef(new Set());
  const [selectedCompetition, setSelectedCompetition] = useState([]);
  const [selectedMatchCount, setSelectedMatchCount] = useState(null);
  const [selectedMatches, setSelectedMatches] = useState([]);
  const [parameterGroups, setParameterGroups] = useState([]);
  const [selectedParameters, setSelectedParameters] = useState({
    checkedIds: [],
    params: [],
  });
  const [allParameterIds, setAllParameterIds] = useState([]);

  const selectedSeasonItems = useMemo(() => {
    if (!Array.isArray(availableSeasons) || availableSeasons.length === 0) {
      return [];
    }

    const normalizedSeasonIds = normalizeSeasonSelectionIds(selectedSeasonIds);
    const matchedSeasons = normalizedSeasonIds
      .map((seasonId) =>
        availableSeasons.find((item) => Number(item?.id) === Number(seasonId))
      )
      .filter(Boolean);

    if (matchedSeasons.length > 0) {
      return matchedSeasons;
    }

    // No season specified yet (initial state): default to the newest available
    // season for display. But if a season *is* specified and simply isn't in
    // the available list — e.g. the backend resolved to a not-yet-started
    // season that has no videos and is therefore excluded from the dropdown —
    // do NOT silently fall back to availableSeasons[0]; that mislabels the
    // header as the newest listed season. Return empty so the UI shows the
    // placeholder instead of a wrong season name.
    if (normalizedSeasonIds.length === 0) {
      return availableSeasons[0] ? [availableSeasons[0]] : [];
    }

    return [];
  }, [availableSeasons, selectedSeasonIds]);

  const selectedSeasonIdList = useMemo(
    () => normalizeSeasonSelectionIds(selectedSeasonIds),
    [selectedSeasonIds]
  );

  const competitions = useMemo(
    () => deriveCompetitionsFromMatches(matches),
    [matches]
  );

  const matchesCountList = useMemo(
    () => createMatchesCountOptions(matches.length, l),
    [matches.length, l]
  );
  const selectedMatchIds = useMemo(
    () => getSelectedMatchIds(selectedMatches),
    [selectedMatches]
  );

  useEffect(() => {
    let isCancelled = false;

    const loadTeamParameters = async () => {
      try {
        setLoadingParameters(true);
        let parameterGroups = null;

        // Use pre-loaded parameters from v6 if available (skips network call)
        if (initialParameters && Array.isArray(initialParameters) && initialParameters.length > 0) {
          parameterGroups = groupTeamParameters(initialParameters);
        } else if (sessionStorage.getItem("teamParameters")) {
          parameterGroups = excludeAttackingTypesParameterGroups(
            JSON.parse(sessionStorage.getItem("teamParameters"))
          );
        } else {
          const response = await $apiV1.get("/stats/parameters/team/");
          const data = response?.data?.results;
          if (data) {
            parameterGroups = groupTeamParameters(data);
          }
        }

        if (!parameterGroups || isCancelled) {
          return;
        }

        parameterGroups = applyDefaultMatchesParameterSelection(parameterGroups);
        sessionStorage.setItem("teamParameters", JSON.stringify(parameterGroups));

        const normalizedData = buildSelectedParametersFromGroups(parameterGroups);
        setParameterGroups(parameterGroups);
        // Use stable reference: if the sorted ID list is identical to what's already
        // in state, keep the previous array object so downstream effects do not
        // re-run unnecessarily when v6 re-hydrates parameters.
        setAllParameterIds((prev) => {
          const prevKey = prev.slice().sort((a, b) => a - b).join(",");
          const nextKey = normalizedData.allIds.slice().sort((a, b) => a - b).join(",");
          return prevKey === nextKey ? prev : normalizedData.allIds;
        });
        setSelectedParameters(normalizedData.selectedParameters);
      } catch (error) {
        console.error("Error in receiving team parameters.", error);
      } finally {
        if (!isCancelled) {
          setLoadingParameters(false);
        }
      }
    };

    loadTeamParameters();

    return () => {
      isCancelled = true;
    };
  }, [initialParameters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset the initial-matches-consumed flag whenever the season selection changes
  // OR a new overview/shell payload arrives (new `initialMatches` reference).
  // The flag exists to consume each payload exactly once; keying it only on the
  // season meant that if an early/empty payload was consumed during a render gap
  // on a season change, the correct payload that arrived later for the same
  // selection was ignored (guard at the top of the consume effect) — leaving
  // `matches` (and the derived competitions) empty until a manual page refresh.
  useEffect(() => {
    initialMatchesConsumedRef.current = false;
  }, [selectedSeasonIdList.join(","), initialMatches]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!teamId || selectedSeasonIdList.length === 0) {
      setMatches([]);
      setSelectedCompetition([]);
      setSelectedMatchCount(null);
      setSelectedMatches([]);
      return;
    }

    // Wait for v6 data to arrive before fetching matches; it will supply them directly
    if (isLoadingInitialData) {
      return;
    }

    // Team page bootstrap is overview-driven only.
    // No automatic matches_opt fallback is allowed after overview.
    if (!Array.isArray(initialMatches)) {
      setMatches([]);
      setSelectedCompetition([]);
      setSelectedMatchCount(null);
      setSelectedMatches([]);
      return;
    }

    // Parameter metadata can still be loading for a moment even when matches
    // are already preloaded via overview. Wait until parameters are ready,
    // but do not trigger any network fallback.
    if (allParameterIds.length === 0) {
      return;
    }

    // Only consume each overview payload once per season selection.
    if (initialMatchesConsumedRef.current) {
      return;
    }

    initialMatchesConsumedRef.current = true;
    const sortedMatches = [...initialMatches].sort(
      (a, b) => new Date(b?.date) - new Date(a?.date)
    );
    const nextCompetitions = deriveCompetitionsFromMatches(sortedMatches);

    setMatches(sortedMatches);
    setSelectedCompetition((previousCompetition) => {
      if (!Array.isArray(previousCompetition) || previousCompetition.length === 0) {
        return nextCompetitions;
      }

      const nextCompetitionIds = new Set(
        nextCompetitions.map((item) => Number(item?.competition_id))
      );
      const nextSelectedCompetitions = previousCompetition.filter((item) =>
        nextCompetitionIds.has(Number(item?.competition_id))
      );

      return nextSelectedCompetitions.length > 0
        ? nextSelectedCompetitions
        : nextCompetitions;
    });
    setSelectedMatchCount(
      createMatchesCountOptions(sortedMatches.length, l).find((item) => item.id === 1) ||
        null
    );
  }, [allParameterIds, initialMatches, isLoadingInitialData, l, selectedSeasonIdList, teamId]);

  // Enrich matches with parameters from matches_opt endpoint if not already loaded
  useEffect(() => {
    if (!shouldEnrichMatches) {
      return;
    }

    const selectedParameterIds = selectedParameters.checkedIds.length > 0
      ? selectedParameters.checkedIds
      : allParameterIds.slice(0, DEFAULT_TEAM_MATCHES_PARAMETER_LIMIT);

    const matchesToEnrich = matches.length
      ? matches
      : (Array.isArray(initialMatches) ? initialMatches : []);

    if (!matchesToEnrich.length || !selectedSeasonIdList.length || !selectedParameterIds.length) {
      return;
    }

    const requestedParamKey = selectedParameterIds.slice().sort((a, b) => a - b).join(",");
    const enrichmentRequestKey = [
      teamId,
      selectedSeasonIdList.join(","),
      requestedParamKey,
    ].join(":");

    if (
      completedEnrichmentRequestKeysRef.current.has(enrichmentRequestKey) ||
      inFlightEnrichmentRequestKeysRef.current.has(enrichmentRequestKey)
    ) {
      return;
    }

    const matchesHaveRequestedParameters = matchesToEnrich.every((match) => {
      const matchParamIds = new Set(
        (match?.parameters || []).map((parameter) =>
          Number(parameter?.parameter_id ?? parameter?.id)
        )
      );

      return selectedParameterIds.every((parameterId) =>
        matchParamIds.has(Number(parameterId))
      );
    });
    if (matchesHaveRequestedParameters) {
      return;
    }

    let isCancelled = false;
    inFlightEnrichmentRequestKeysRef.current.add(enrichmentRequestKey);

    const enrichMatches = async () => {
      try {
        const responses = await Promise.all(
          selectedSeasonIdList.map((seasonId) =>
            $apiV2.get(
              `/platform/team/${teamId}/matches_opt?year_season_id=${seasonId}&params_ids=${requestedParamKey}`
            )
          )
        );

        if (isCancelled) {
          return;
        }

        const enrichedData = getUniqueArrayObjectsByKey(
          responses.flatMap((response) =>
            Array.isArray(response?.data) ? response.data : []
          ),
          "id"
        ) || [];

        // Merge parameters into existing matches
        const enrichedMatches = matchesToEnrich.map((match) => {
          const enrichedMatch = enrichedData.find((m) => m?.id === match?.id);
          return enrichedMatch ? enrichedMatch : match;
        });

        completedEnrichmentRequestKeysRef.current.add(enrichmentRequestKey);
        setMatches(enrichedMatches);
      } catch (error) {
        console.debug("Failed to enrich matches with parameters:", error);
      } finally {
        inFlightEnrichmentRequestKeysRef.current.delete(enrichmentRequestKey);
      }
    };

    enrichMatches();

    return () => {
      isCancelled = true;
    };
  }, [allParameterIds, initialMatches, matches, selectedParameters.checkedIds, selectedSeasonIdList, shouldEnrichMatches, teamId]);

  useEffect(() => {
    setSelectedMatches(
      deriveSelectedMatches({
        matches,
        selectedCompetition,
        selectedMatchCount,
      })
    );
  }, [matches, selectedCompetition, selectedMatchCount]);

  const selectSeason = useCallback(
    (season) => {
      if (!season?.id) {
        return;
      }

      const normalizedSeasonId = Number(season.id);
      const isSelected = selectedSeasonIdList.includes(normalizedSeasonId);
      const nextSeasonIds = isSelected
        ? selectedSeasonIdList.filter((item) => item !== normalizedSeasonId)
        : [...selectedSeasonIdList, normalizedSeasonId];

      if (nextSeasonIds.length === 0) {
        return;
      }

      syncSelectedSeasons?.(nextSeasonIds);
    },
    [selectedSeasonIdList, syncSelectedSeasons]
  );

  const toggleCompetition = useCallback((competition) => {
    setSelectedCompetition((prev) => {
      const isSelected = prev.some(
        (selected) =>
          Number(selected?.competition_id) === Number(competition?.competition_id)
      );

      if (isSelected) {
        return prev.filter(
          (selected) =>
            Number(selected?.competition_id) !== Number(competition?.competition_id)
        );
      }

      return [...prev, competition];
    });
  }, []);

  const selectMatchCount = useCallback((matchCountOption) => {
    setSelectedMatchCount(matchCountOption);
  }, []);

  const toggleMatch = useCallback((match) => {
    setSelectedMatches((prev) => {
      const isSelected = prev.some((selected) => selected?.id === match?.id);

      if (isSelected) {
        return prev.filter((selected) => selected?.id !== match?.id);
      }

      return [...prev, match];
    });
  }, []);

  const changeParameters = useCallback((parameterGroups) => {
    const normalizedData = buildSelectedParametersFromGroups(parameterGroups);
    setParameterGroups(parameterGroups);
    setSelectedParameters(normalizedData.selectedParameters);
  }, []);

  const resetFilters = useCallback(() => {
    setSelectedCompetition(competitions);
    setSelectedMatchCount(
      createMatchesCountOptions(matches.length, l).find((item) => item.id === 1) || null
    );
  }, [competitions, l, matches.length]);

  const applyTemplate = useCallback(
    ({ competitionIds, matchCountId, parameterIds } = {}) => {
      if (Array.isArray(competitionIds)) {
        const competitionIdSet = new Set(competitionIds.map(Number));
        const nextCompetitions = competitions.filter((c) =>
          competitionIdSet.has(Number(c.competition_id))
        );
        setSelectedCompetition(
          nextCompetitions.length > 0 ? nextCompetitions : competitions
        );
      }

      if (matchCountId != null) {
        const nextMatchCount = matchesCountList.find((o) => o.id === matchCountId);
        if (nextMatchCount) {
          setSelectedMatchCount(nextMatchCount);
        }
      }

      if (Array.isArray(parameterIds)) {
        const {
          nextParameterGroups,
          selectedParameters: nextSelectedParameters,
        } = applyTemplateParameterIdsToGroups(parameterGroups, parameterIds);

        setParameterGroups(nextParameterGroups);
        setSelectedParameters(nextSelectedParameters);
        sessionStorage.setItem("teamParameters", JSON.stringify(nextParameterGroups));
      }
    },
    [competitions, matchesCountList, parameterGroups]
  );

  return {
    loadingMatches: loadingParameters,
    availableSeasons,
    selectedSeasonItems,
    competitions,
    selectedCompetition,
    matches,
    matchesCountList,
    selectedMatchCount,
    selectedMatches,
    selectedMatchIds,
    selectedParameters,
    parameterGroups,
    allParameterIds,
    actions: {
      selectSeason,
      toggleCompetition,
      selectMatchCount,
      toggleMatch,
      changeParameters,
      resetFilters,
      applyTemplate,
    },
  };
};

export default useTeamPageFilterController;
